import { giphyDarkestGrey } from '@giphy/colors'
import { ReactNode, SyntheticEvent, useContext, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import MessageContext from 'shared/contexts/message'
import styled from 'styled-components'
import { useCreationNextStep } from '../hooks/use-creation-next-step'
import { CreationToolsContext } from '../manager'
import { getBasePathFromCreationTool } from '../routes'
import { CreationToolFrame } from '../types'
import CreationToolGAEvent from '../types/ga-event'
import CreationToolGradient from '../types/gradient'
import CreationToolStep from '../types/steps'
import { GradientButton } from './buttons'

const Container = styled.div`
    background: ${giphyDarkestGrey};
    bottom: 0;
    padding-bottom: 10px;
    position: sticky;
    width: 100%;
`

type Props = {
    className?: string
    children?: ReactNode
    active?: boolean
    frames?: CreationToolFrame[]
    gradient?: CreationToolGradient
    gaEventAction: CreationToolStep
    preNavigateAction?: () => Promise<void>
}

const CreationToolsContinueButton = ({
    className,
    children,
    active = false,
    frames,
    gradient = CreationToolGradient.IndigoPurple,
    gaEventAction,
    preNavigateAction,
}: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const { outputType, tool, steps, saveFrames, trackEvent } = useContext(CreationToolsContext)
    const [isWaiting, setIsWaiting] = useState<boolean>(false)
    const history = useHistory()
    const nextStep = useCreationNextStep(steps)
    const basePath = getBasePathFromCreationTool(tool)
    const nextUrl = nextStep ? `${basePath}${CreationToolStep.route(nextStep)}` : ''

    const onClick = async (e: SyntheticEvent<HTMLElement, MouseEvent>) => {
        const action = CreationToolGAEvent.creationToolAction(gaEventAction)
        if (action) {
            trackEvent(
                CreationToolGAEvent.outputTypeCategory(outputType),
                action,
                CreationToolGAEvent.nextStepLabel(nextStep)
            )
        }
        frames && nextStep && saveFrames(frames, nextStep)

        if (preNavigateAction) {
            e.preventDefault()
            setIsWaiting(true)
            try {
                await preNavigateAction()
                setIsWaiting(false)
                nextUrl && history.replace(nextUrl)
            } catch (err) {
                sendMessage(err.message || 'An error occurred', 'error')
                setIsWaiting(false)
            }
        }
    }

    return nextStep ? (
        <Container className={className}>
            <GradientButton
                {...{
                    as: Link,
                    to: nextUrl,
                    disabled: !active || isWaiting,
                    colors: CreationToolGradient.colors(gradient),
                    onClick,
                }}
            >
                {children || `Continue to ${CreationToolStep.label(nextStep)}`}
            </GradientButton>
        </Container>
    ) : null
}

export default CreationToolsContinueButton
